import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
// TODO: Re-enable when MCP manager button is restored
// import { PlugZap } from "@/components/animate-ui/icons/plug-zap";
import { Search } from "@/components/animate-ui/icons/search";
import { X } from "@/components/animate-ui/icons/x";
import { ApiKeyDialog } from "@/components/integrations/api-key-dialog";
import { ComposioFieldsDialog } from "@/components/integrations/composio-fields-dialog";
import { GoogleAdsCustomerSelect } from "@/components/integrations/google-ads-customer-select";
import { useGoogleAdsOAuth } from "@/components/integrations/google-ads-oauth";
import { useGoogleSearchConsoleOAuth } from "@/components/integrations/google-search-console-oauth";
import { IntegrationCard } from "@/components/integrations/integration-card";
import { McpSetupDialog } from "@/components/integrations/mcp-setup-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { USE_CASE_CATEGORIES, type UseCaseCategory } from "@/constants/integrations";
import { authClient } from "@/lib/api/auth-client";
import {
	type ComposioRequiredFieldsError,
	completeComposioCallback,
	connectWithApiKey,
	deleteGoogleAdsIntegration,
	deleteGoogleSearchConsoleIntegration,
	disconnectIntegration,
	getAppIntegrations,
	getMcpConfig,
	getUserIntegrations,
	initComposioOAuth,
} from "@/lib/api/integrations";
import { integrationKeys } from "@/lib/api/query-keys/integrations";
import type { components } from "@/lib/api/types";
import { captureFrontendError, useMutationWithToast } from "@/lib/errors";
import { buildMetaTags } from "@/lib/seo/meta";
import { filterAndSortIntegrations } from "@/lib/utils/integrations";

export const Route = createFileRoute("/_dashboard/integrations")({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			oauth: (search.oauth as string) || undefined,
			provider: (search.provider as string) || undefined,
			message: (search.message as string) || undefined,
			// Composio callback params
			composio_connection_id: (search.composio_connection_id as string) || undefined,
			composio_app_integration_id: (search.composio_app_integration_id as string) || undefined,
		};
	},

	head: () => {
		const meta = buildMetaTags({
			title: "Apps",
			description: "Connect your tools and services",
			noIndex: true,
			noFollow: true,
		});

		return { meta };
	},

	component: IntegrationsPage,
});

function IntegrationsPage() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const search = Route.useSearch();

	const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
	const [showCustomerSelect, setShowCustomerSelect] = useState(false);
	const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
	const [selectedIntegration, setSelectedIntegration] = useState<
		components["schemas"]["AppIntegrationResponse"] | null
	>(null);
	const [shouldAutoRefresh, setShouldAutoRefresh] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [showMcpSetupDialog, setShowMcpSetupDialog] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState<UseCaseCategory>("All");
	const [showComposioFieldsDialog, setShowComposioFieldsDialog] = useState(false);
	const [composioFields, setComposioFields] = useState<
		{ name: string; displayName: string; description: string }[]
	>([]);
	const [composioFieldsLoading, setComposioFieldsLoading] = useState(false);

	// Fetch catalog of all available integrations
	const { data: catalog, isLoading: catalogLoading } = useQuery({
		queryKey: integrationKeys.catalog(),
		queryFn: getAppIntegrations,
		staleTime: 5 * 60 * 1000,
	});

	// Fetch user's connected integrations
	const { data: userIntegrations, isLoading: userIntegrationsLoading } = useQuery({
		queryKey: integrationKeys.user(),
		queryFn: getUserIntegrations,
		staleTime: 60 * 1000,
	});

	// Fetch MCP configuration (only if user has integrations)
	const { data: mcpConfig } = useQuery({
		queryKey: integrationKeys.mcpConfig(),
		queryFn: getMcpConfig,
		enabled: (userIntegrations?.length ?? 0) > 0,
		staleTime: 60 * 1000,
	});

	// Build set of connected provider:kind combinations
	const connectedProviderKinds = useMemo(() => {
		if (!userIntegrations) return new Set<string>();
		return new Set(userIntegrations.map((ui) => `${ui.provider}:${ui.kind}`));
	}, [userIntegrations]);

	// Filter and sort integrations
	const filteredCatalog = useMemo(() => {
		if (!catalog) return [];
		return filterAndSortIntegrations(
			catalog,
			selectedCategory,
			searchQuery,
			connectedProviderKinds
		);
	}, [catalog, selectedCategory, searchQuery, connectedProviderKinds]);

	// Handle Composio OAuth callback (user returned from OAuth)
	useEffect(() => {
		const pendingConnectionId = sessionStorage.getItem("composio_pending_connection_id");
		const pendingAppIntegrationId = sessionStorage.getItem("composio_pending_app_integration_id");
		const pendingProvider = sessionStorage.getItem("composio_pending_provider");

		if (pendingConnectionId && pendingAppIntegrationId) {
			// Clear session storage first
			sessionStorage.removeItem("composio_pending_connection_id");
			sessionStorage.removeItem("composio_pending_app_integration_id");
			sessionStorage.removeItem("composio_pending_provider");

			// Complete the Composio callback
			completeComposioCallback(Number(pendingAppIntegrationId), pendingConnectionId)
				.then(() => {
					queryClient.invalidateQueries({ queryKey: integrationKeys.user() });
					toast.success(`${pendingProvider || "Integration"} connected successfully!`);
				})
				.catch((error) => {
					captureFrontendError(error, {
						area: "integrations",
						action: "composio_callback_complete",
					});
					toast.error("Failed to complete connection. Please try again.");
				});
		}
	}, [queryClient]);

	// Handle OAuth callback results from URL params
	useEffect(() => {
		if (search.oauth && search.provider) {
			if (search.oauth === "success") {
				// OAuth successful - invalidate and refetch
				queryClient.invalidateQueries({
					queryKey: integrationKeys.user(),
				});

				// Refetch integrations to check if customer selection is needed
				queryClient
					.fetchQuery({
						queryKey: integrationKeys.user(),
						queryFn: getUserIntegrations,
					})
					.then((integrations) => {
						// Check if this is Google Ads integration
						if (search.provider === "google-ads") {
							const googleAdsIntegration = integrations?.find(
								(i) => i.provider === "google" && i.kind === "ads"
							);

							// If no customer ID set, show customer selection dialog
							if (googleAdsIntegration && !googleAdsIntegration.account_id) {
								setShouldAutoRefresh(true); // Auto-refresh after OAuth to fetch fresh customer list
								setShowCustomerSelect(true);
							}
						}

						toast.success(
							`${
								search.provider === "google-ads" ? "Google Ads" : search.provider
							} connected successfully!`
						);
					});
			} else if (search.oauth === "error") {
				// OAuth failed
				const errorMessage = search.message || "Failed to connect integration";
				toast.error(errorMessage);
			}

			// Clear URL params after showing status
			navigate({
				to: "/integrations",
				search: {
					oauth: undefined,
					provider: undefined,
					message: undefined,
					composio_connection_id: undefined,
					composio_app_integration_id: undefined,
				},
				replace: true,
			});
		}
	}, [search, navigate, queryClient]);

	// Google Ads disconnect mutation (using new helper)
	const disconnectMutation = useMutationWithToast(deleteGoogleAdsIntegration, {
		successMessage: "Google Ads disconnected successfully",
		onSuccess: async () => {
			// Invalidate and refetch to update UI
			await queryClient.invalidateQueries({
				queryKey: integrationKeys.user(),
			});
			setShowDisconnectDialog(false);
		},
	});

	// Google Search Console disconnect mutation
	const disconnectGscMutation = useMutationWithToast(deleteGoogleSearchConsoleIntegration, {
		successMessage: "Google Search Console disconnected successfully",
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: integrationKeys.user(),
			});
		},
	});

	// API key connect mutation (using new helper)
	const connectApiKeyMutation = useMutationWithToast(
		({
			appIntegrationId,
			apiKey,
			options,
		}: {
			appIntegrationId: number;
			apiKey: string;
			options?: Record<string, string>;
		}) => connectWithApiKey(appIntegrationId, apiKey, options),
		{
			successMessage: "Integration connected successfully!",
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: integrationKeys.user(),
				});
				setShowApiKeyDialog(false);
				setSelectedIntegration(null);
			},
		}
	);

	// Generic disconnect mutation (using new helper)
	const disconnectGenericMutation = useMutationWithToast(
		(appIntegrationId: number) => disconnectIntegration(appIntegrationId),
		{
			successMessage: "Integration disconnected successfully",
			onSuccess: async () => {
				await queryClient.invalidateQueries({
					queryKey: integrationKeys.user(),
				});
			},
		}
	);

	// Google Ads OAuth hook
	const { startOAuthFlow: startGoogleAdsOAuth, isLoading: googleAdsOAuthLoading } =
		useGoogleAdsOAuth();

	// Google Search Console OAuth hook
	const { startOAuthFlow: startGscOAuth, isLoading: gscOAuthLoading } =
		useGoogleSearchConsoleOAuth();

	// External OAuth flow handler
	const startExternalOAuthFlow = async (appIntegrationId: number) => {
		try {
			const redirectUri = `${window.location.origin}/integrations/callback`;

			const response = await authClient.POST(
				"/api/v1/integrations/{app_integration_id}/oauth/init",
				{
					params: { path: { app_integration_id: appIntegrationId } },
					body: { redirect_uri: redirectUri },
				}
			);

			if (response.error || !response.data) {
				throw new Error("Failed to initialize OAuth flow");
			}

			const { authorization_url } = response.data as { authorization_url: string };

			// Redirect to provider's OAuth page
			window.location.href = authorization_url;
		} catch (error) {
			toast.error("Failed to start OAuth flow");
			captureFrontendError(error, {
				area: "integrations",
				action: "external_oauth_start",
				appIntegrationId,
			});
		}
	};

	// Composio OAuth flow handler
	const startComposioOAuthFlow = async (
		appIntegrationId: number,
		providerName: string,
		connectionData?: Record<string, string>
	) => {
		try {
			const redirectUri = `${window.location.origin}/integrations?composio_connection_id=PLACEHOLDER&composio_app_integration_id=${appIntegrationId}`;

			const { redirect_url, connection_id } = await initComposioOAuth(
				appIntegrationId,
				redirectUri,
				connectionData
			);

			sessionStorage.setItem("composio_pending_connection_id", connection_id);
			sessionStorage.setItem("composio_pending_app_integration_id", String(appIntegrationId));
			sessionStorage.setItem("composio_pending_provider", providerName);

			window.location.href = redirect_url;
		} catch (error) {
			const fieldsError = error as ComposioRequiredFieldsError;
			if (fieldsError.status === 422 && fieldsError.required_fields) {
				setComposioFields(fieldsError.required_fields);
				// Find the integration to set selectedIntegration for the dialog
				const integration = catalog?.find((i) => i.id === appIntegrationId);
				if (integration) setSelectedIntegration(integration);
				setShowComposioFieldsDialog(true);
				return;
			}
			toast.error("Failed to start Composio OAuth flow");
			captureFrontendError(error, {
				area: "integrations",
				action: "composio_oauth_start",
				appIntegrationId,
			});
		}
	};

	const handleCustomerSelected = async () => {
		setShowCustomerSelect(false);
		// Invalidate to update UI with selected customer ID
		await queryClient.invalidateQueries({
			queryKey: integrationKeys.user(),
		});
		toast.success("Customer ID updated successfully!");
	};

	const isLoading = catalogLoading || userIntegrationsLoading;

	return (
		<>
			<div className="container mx-auto px-4 py-6 max-w-6xl">
				<div className="mb-8">
					<div className="flex items-center justify-between">
						<h1 className="text-3xl font-bold tracking-tight">Apps</h1>
						{/* TODO: Re-enable MCP manager button after testing
						{userIntegrations && userIntegrations.length > 0 && (
							<Button onClick={() => setShowMcpSetupDialog(true)}>
								<PlugZap className="mr-2 h-4 w-4" />
								Set up MCP server
							</Button>
						)}
						*/}
					</div>
				</div>

				{/* Search bar */}
				<div className="mb-4">
					<div className="relative">
						<Search
							className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
							animateOnHover
						/>
						<Input
							type="text"
							placeholder="Search apps..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="pl-9 pr-9"
						/>
						{searchQuery && (
							<button
								type="button"
								onClick={() => setSearchQuery("")}
								className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
							>
								<X className="size-4" animateOnHover />
							</button>
						)}
					</div>
				</div>

				{/* Category filter */}
				<div className="mb-6 flex flex-wrap items-center gap-2">
					{USE_CASE_CATEGORIES.map((category) => (
						<Button
							key={category}
							variant={selectedCategory === category ? "default" : "outline"}
							size="sm"
							onClick={() => setSelectedCategory(category)}
						>
							{category}
						</Button>
					))}
				</div>

				<div className="space-y-6">
					{isLoading ? (
						<div className="space-y-4">
							<Skeleton className="h-32 w-full" />
							<Skeleton className="h-32 w-full" />
						</div>
					) : filteredCatalog.length > 0 ? (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{filteredCatalog.map((integration) => {
								// Check if this integration is connected
								const userIntegration = userIntegrations?.find(
									(ui) => ui.provider === integration.provider && ui.kind === integration.kind
								);
								const isConnected = !!userIntegration;

								// Determine handlers based on auth_type
								const isGoogleAds = integration.provider === "google" && integration.kind === "ads";

								const handleConnect = () => {
									if (integration.auth_type === "oauth2") {
										// Internal OAuth - differentiate by provider/kind
										if (
											integration.provider === "google" &&
											integration.kind === "search_console"
										) {
											startGscOAuth();
										} else {
											// Default to Google Ads for backwards compatibility
											startGoogleAdsOAuth();
										}
									} else if (integration.auth_type === "external_oauth") {
										// External OAuth (Asana, Linear, etc.) - new flow
										startExternalOAuthFlow(integration.id);
									} else if (
										integration.auth_type === "api_key" ||
										integration.auth_type === "api_key_proxy"
									) {
										// API key flow (Stripe, Gemini, etc.)
										setSelectedIntegration(integration);
										setShowApiKeyDialog(true);
									} else if (integration.auth_type === "composio") {
										// Composio OAuth flow (Gmail, Slack, etc.)
										startComposioOAuthFlow(integration.id, integration.name);
									} else {
										toast.info("This integration is coming soon!");
									}
								};

								const handleDisconnect = () => {
									if (isGoogleAds) {
										// Google Ads has special disconnect flow with customer selection
										setShowDisconnectDialog(true);
									} else if (
										integration.provider === "google" &&
										integration.kind === "search_console"
									) {
										disconnectGscMutation.mutate();
									} else if (
										integration.auth_type === "api_key" ||
										integration.auth_type === "api_key_proxy" ||
										integration.auth_type === "external_oauth" ||
										integration.auth_type === "composio"
									) {
										// Generic disconnect for API key + external OAuth + Composio integrations
										disconnectGenericMutation.mutate(integration.id);
									} else {
										toast.info("This integration is coming soon!");
									}
								};

								const handleChangeCustomer = isGoogleAds
									? () => {
											setShouldAutoRefresh(false); // Don't auto-refresh when manually opening settings
											setShowCustomerSelect(true);
										}
									: undefined;

								// Prepare metadata for display
								const metadata =
									isConnected && userIntegration
										? {
												customer_id: userIntegration.account_id || undefined,
												accessible_customers:
													(
														userIntegration.meta_json as unknown as {
															accessible_customers?: string[];
														}
													)?.accessible_customers || [],
												scopes: userIntegration.access_scopes || [],
											}
										: undefined;

								return (
									<IntegrationCard
										key={integration.id}
										name={integration.name}
										provider={integration.provider}
										kind={integration.kind}
										isConnected={isConnected}
										lastSynced={
											userIntegration
												? userIntegration.updated_at
													? new Date(userIntegration.updated_at).toLocaleDateString()
													: undefined
												: undefined
										}
										onConnect={handleConnect}
										onDisconnect={handleDisconnect}
										onChangeCustomer={handleChangeCustomer}
										isLoading={
											googleAdsOAuthLoading ||
											gscOAuthLoading ||
											disconnectMutation.isPending ||
											disconnectGscMutation.isPending
										}
										logoUrl={integration.logo_url ?? undefined}
										metadata={metadata}
									/>
								);
							})}
						</div>
					) : (
						<Alert>
							<AlertDescription>
								{catalog && catalog.length > 0
									? "No integrations match your search."
									: "No integrations available at this time. Check back soon!"}
							</AlertDescription>
						</Alert>
					)}
				</div>
			</div>

			{/* MCP Setup Dialog */}
			{userIntegrations && userIntegrations.length > 0 && (
				<McpSetupDialog
					open={showMcpSetupDialog}
					onClose={() => setShowMcpSetupDialog(false)}
					mcpConfig={mcpConfig}
				/>
			)}

			{/* Customer Selection Dialog */}
			<GoogleAdsCustomerSelect
				open={showCustomerSelect}
				onClose={() => setShowCustomerSelect(false)}
				onSuccess={handleCustomerSelected}
				shouldAutoRefresh={shouldAutoRefresh}
			/>

			{/* API Key Dialog */}
			{selectedIntegration && (
				<ApiKeyDialog
					open={showApiKeyDialog}
					onClose={() => {
						setShowApiKeyDialog(false);
						setSelectedIntegration(null);
					}}
					onSubmit={(apiKey, options) => {
						connectApiKeyMutation.mutate({
							appIntegrationId: selectedIntegration.id,
							apiKey,
							options,
						});
					}}
					integrationName={selectedIntegration.name}
					isLoading={connectApiKeyMutation.isPending}
					documentationUrl={selectedIntegration.api_key_documentation_url ?? undefined}
					additionalGuidance={selectedIntegration.api_key_guidance ?? undefined}
					inputLabel={selectedIntegration.api_key_label ?? undefined}
					regionOptions={selectedIntegration.region_options ?? undefined}
				/>
			)}

			{/* Composio Required Fields Dialog */}
			{selectedIntegration && (
				<ComposioFieldsDialog
					open={showComposioFieldsDialog}
					onClose={() => {
						setShowComposioFieldsDialog(false);
						setSelectedIntegration(null);
						setComposioFields([]);
					}}
					onSubmit={async (data) => {
						setComposioFieldsLoading(true);
						await startComposioOAuthFlow(selectedIntegration.id, selectedIntegration.name, data);
						setComposioFieldsLoading(false);
					}}
					integrationName={selectedIntegration.name}
					isLoading={composioFieldsLoading}
					fields={composioFields}
				/>
			)}

			{/* Disconnect Confirmation Dialog */}
			<Dialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Disconnect Google Ads?</DialogTitle>
						<DialogDescription>
							This will remove your Google Ads integration. Your teammates will no longer be able to
							access Google Ads data.
						</DialogDescription>
					</DialogHeader>

					<DialogFooter>
						<Button variant="outline" onClick={() => setShowDisconnectDialog(false)}>
							Cancel
						</Button>
						<Button
							variant="destructive"
							onClick={() => disconnectMutation.mutate()}
							disabled={disconnectMutation.isPending}
						>
							Disconnect
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
